import { useMemo } from 'react';

export const useGoalsData = (
  studentGoalsData,
  user,
  currentUser,
  formState,
  goalsSearchTerm,
  goalsExpanded,
  sessionData
) => {
  const selectedGoalIds = useMemo(() => {
    if (!Array.isArray(sessionData?.selectedGoals)) return [];
    return sessionData.selectedGoals.filter(Boolean);
  }, [sessionData?.selectedGoals]);

  const selectedGoalIdsSet = useMemo(
    () => new Set(selectedGoalIds),
    [selectedGoalIds]
  );

  // Process goals for the specific student with access control
  const studentGoals = useMemo(() => {
    const studentId = formState.studentId || user?.studentId || user?.id;

    if (!studentId || !studentGoalsData || !currentUser?.uid) return [];

    const student = studentGoalsData.find((s) => s.id === studentId);
    if (!student) return [];

    const goals = [];
    if (Array.isArray(student.dataCollection)) {
      student.dataCollection.forEach((collection) => {
        // Check access control
        const goalId = collection?.id || collection?.docId;
        if (!goalId) return;

        const hasStandardAccess = (() => {
          if (!collection.info || !collection.info.ShareWithTeam) {
            // Allow owner to always see their own goal
            const isOwner =
              collection.info?.GoalDetails?.GoalAuthor === currentUser.uid;
            return isOwner;
          }

          const { EditAccess = [], ViewOnlyAccess = [] } =
            collection.info.ShareWithTeam;
          const hasEditAccess = EditAccess.some(
            (access) =>
              access.uid === currentUser.uid && access.approved === true
          );
          const hasViewAccess = ViewOnlyAccess.some(
            (access) =>
              access.uid === currentUser.uid && access.approved === true
          );

       

          // Owner should always retain access
          const isOwner =
            collection.info?.GoalDetails?.GoalAuthor === currentUser.uid;
          return isOwner || hasEditAccess || hasViewAccess;
        })();

        const hasForcedAccess = selectedGoalIdsSet.has(goalId);
        const hasAccess = hasStandardAccess || hasForcedAccess;

        if (!hasAccess) return;

        const goalTitle =
          collection?.info?.GoalDetails?.GoalTitle || 'Untitled Goal';

        if (goalTitle && goalId && !collection.isArchived) {
          goals.push({
            id: goalId,
            title: goalTitle,
            category: collection?.info?.GoalDetails?.GoalCategory || '',
            isRestricted: !hasStandardAccess && hasForcedAccess,
          });
        }
      });
    }

    return goals;
  }, [
    studentGoalsData,
    user,
    currentUser?.uid,
    formState,
    selectedGoalIdsSet,
  ]);

  // Filter goals based on search
  const filteredGoals = useMemo(() => {
    if (!goalsSearchTerm) return studentGoals;
    const searchLower = goalsSearchTerm.toLowerCase();
    return studentGoals.filter((goal) =>
      goal.title?.toLowerCase().includes(searchLower)
    );
  }, [studentGoals, goalsSearchTerm]);

  // Displayed goals (max 10 or all if expanded)
  const displayedGoals = goalsExpanded
    ? filteredGoals
    : filteredGoals.slice(0, 10);

  const originalSessionGoals = useMemo(() => {
    const goalsFromSession =
      sessionData?.originalSessionData?.Goals ||
      sessionData?.originalGoalData ||
      [];
    return Array.isArray(goalsFromSession) ? goalsFromSession : [];
  }, [sessionData?.originalSessionData?.Goals, sessionData?.originalGoalData]);

  const originalGoalsById = useMemo(() => {
    return originalSessionGoals.reduce((acc, goalItem) => {
      if (!goalItem) return acc;
      const goalId =
        goalItem.goalID ||
        goalItem.goalId ||
        goalItem.id ||
        (typeof goalItem === 'string' ? goalItem : null);
      if (!goalId) return acc;
      acc[goalId] = goalItem;
      return acc;
    }, {});
  }, [originalSessionGoals]);

  // Reconstruct selectedGoals from sessionData (need full goal objects, not just IDs)
  const selectedGoals = useMemo(() => {
    const selectedGoalIds = Array.isArray(sessionData.selectedGoals)
      ? sessionData.selectedGoals
      : [];

    if (selectedGoalIds.length === 0) return [];

    const seen = new Set();

    return selectedGoalIds.reduce((acc, goalId) => {
      if (!goalId || seen.has(goalId)) {
        return acc;
      }

      seen.add(goalId);

      const accessibleGoal = studentGoals.find((goal) => goal.id === goalId);
      if (accessibleGoal) {
        acc.push(accessibleGoal);
        return acc;
      }

      const originalGoal = originalGoalsById[goalId];
      const fallbackTitle =
        originalGoal?.goalTitle ||
        originalGoal?.GoalTitle ||
        originalGoal?.goalName ||
        originalGoal?.name ||
        `Goal (${goalId})`;

      acc.push({
        id: goalId,
        title: fallbackTitle,
        category: originalGoal?.category || '',
        isRestricted: true,
      });

      return acc;
    }, []);
  }, [sessionData.selectedGoals, studentGoals, originalGoalsById]);

  return {
    studentGoals,
    filteredGoals,
    displayedGoals,
    selectedGoals,
  };
};
